import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
  autorizarFacturaAFIP,
  crearFactura,
  emitirNotaCredito,
  descargarFacturaPDF,
  listarFacturas,
  obtenerEstadisticasFacturacion,
  obtenerAfipConfig,
} from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';
import { formatearFechaArgentina } from '../utils/fechas';
import {
  TrashIcon,
  PaperAirplaneIcon,
  ArrowUturnLeftIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  KeyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import ModalAsistenteAfip from '../components/ModalAsistenteAfip';
import { isDemoUser, solicitarRegistroDemo } from '../utils/demoMode';
import { authService } from '../api/auth';
import { obtenerBorradorFactura, limpiarBorradorFactura } from '../utils/facturacionBridge';

const EMPTY_ITEM = { descripcion: '', cantidad: 1, precio_unitario: '', alicuota_iva: 21 };

const OPCIONES_CONDICION_PAGO = [
  'Efectivo',
  'Tarjeta de crédito',
  'Tarjeta de débito',
  'Tarjeta de crédito / efectivo',
  'Transferencia',
];

const Facturacion = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [condPagoCustom, setCondPagoCustom] = useState(false);
  const [stats, setStats] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [afipConfig, setAfipConfig] = useState(null);
  const [modalAsistenteAbierto, setModalAsistenteAbierto] = useState(false);
  const [filtros, setFiltros] = useState({ tipo_factura: '', estado: '', page: 1, page_size: 15 });
  const [modalNC, setModalNC] = useState({
    abierto: false,
    factura: null,
    motivo: 'Devolución de mercadería o anulación de operación',
    subiendo: false,
  });

  const obtenerFechaHoy = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
  };

  const obtenerFechaMinima = () => {
    const hace5Dias = new Date();
    hace5Dias.setDate(hace5Dias.getDate() - 5);
    return hace5Dias.toISOString().split('T')[0];
  };

  const [form, setForm] = useState({
    tipo_factura: 'B',
    fecha_emision: obtenerFechaHoy(),
    cliente_id: null,
    anteojo_id: null,
    cliente: {
      nombre_razon_social: '',
      cuit: '',
      condicion_iva: 'CF',
      email: '',
      domicilio: '',
    },
    items: [{ ...EMPTY_ITEM }],
    condiciones_pago: 'Efectivo',
    observaciones: '',
  });

  const esMonotributista = useMemo(() => {
    const cond = String(afipConfig?.condicion_iva || '').toLowerCase();
    return cond.includes('monotribut');
  }, [afipConfig?.condicion_iva]);

  const esFacturaA = form.tipo_factura === 'A';
  const documentoLabel = esFacturaA ? 'CUIT' : 'DNI';
  const documentoPlaceholder = esFacturaA ? 'XX-XXXXXXXX-X' : 'Ej: 30123456';

  const cambiarTipoFactura = (tipo) => {
    setForm((prev) => ({
      ...prev,
      tipo_factura: tipo,
      items: tipo === 'C' ? prev.items.map((it) => ({ ...it, alicuota_iva: 0 })) : prev.items,
      cliente: {
        ...prev.cliente,
        condicion_iva: tipo === 'A' ? 'RI' : (prev.cliente.condicion_iva === 'RI' ? 'CF' : prev.cliente.condicion_iva),
      },
    }));
  };

  const totalPreview = useMemo(() => {
    const totalLineas = form.items.reduce((acc, item) => {
      const cantidad = Number(item.cantidad) || 0;
      const precio = Number(item.precio_unitario) || 0;
      return acc + cantidad * precio;
    }, 0);

    const iva = form.items.reduce((acc, item) => {
      if (form.tipo_factura === 'C') return 0;
      const linea = (Number(item.cantidad) || 0) * (Number(item.precio_unitario) || 0);
      const alicuota = Number(item.alicuota_iva) || 0;

      if (form.tipo_factura === 'B') {
        if (alicuota <= 0) return acc;
        return acc + (linea - linea / (1 + alicuota / 100));
      }

      return acc + linea * (alicuota / 100);
    }, 0);

    const subtotal = form.tipo_factura === 'C' ? totalLineas : (form.tipo_factura === 'B' ? totalLineas - iva : totalLineas);
    const total = form.tipo_factura === 'C' ? totalLineas : (form.tipo_factura === 'B' ? totalLineas : subtotal + iva);

    return {
      subtotal,
      iva,
      total,
    };
  }, [form.items, form.tipo_factura]);

  const cargarDatos = async (filtrosActuales = filtros) => {
    try {
      setLoading(true);
      const [statsData, facturasData, cfgData] = await Promise.all([
        obtenerEstadisticasFacturacion(),
        listarFacturas(filtrosActuales),
        obtenerAfipConfig(),
      ]);

      setStats(statsData);
      setFacturas(facturasData.items || []);
      setAfipConfig(cfgData);

      const esMono = String(cfgData?.condicion_iva || '').toLowerCase().includes('monotribut');
      if (esMono) {
        setForm((prev) => ({
          ...prev,
          tipo_factura: 'C',
          items: prev.items.map((it) => ({ ...it, alicuota_iva: 0 })),
        }));
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error de facturación',
        text: error.message || 'No se pudieron cargar los datos de facturación',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();

    const borrador = obtenerBorradorFactura();
    if (borrador) {
      setForm((prev) => ({
        ...prev,
        cliente_id: borrador.cliente_id || prev.cliente_id || null,
        anteojo_id: borrador.anteojo_id || prev.anteojo_id || null,
        cliente: {
          ...prev.cliente,
          nombre_razon_social: borrador.cliente?.nombre_razon_social || prev.cliente.nombre_razon_social,
          cuit: borrador.cliente?.cuit || prev.cliente.cuit,
          condicion_iva: borrador.cliente?.condicion_iva || prev.cliente.condicion_iva,
          email: borrador.cliente?.email || prev.cliente.email,
          domicilio: borrador.cliente?.domicilio || prev.cliente.domicilio,
        },
        items: borrador.items && borrador.items.length > 0 ? borrador.items : prev.items,
        condiciones_pago: borrador.condiciones_pago || prev.condiciones_pago,
        observaciones: borrador.observaciones || prev.observaciones,
      }));

      if (borrador.condiciones_pago && !OPCIONES_CONDICION_PAGO.includes(borrador.condiciones_pago)) {
        setCondPagoCustom(true);
      } else {
        setCondPagoCustom(false);
      }

      limpiarBorradorFactura();

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: 'Datos cargados para facturar',
        text: 'Revisá los datos y completá DNI/CUIT si corresponde para emitir.',
        showConfirmButton: false,
        timer: 4000,
      });
    }
  }, []);

  const actualizarCliente = (field, value) => {
    setForm((prev) => ({
      ...prev,
      cliente: {
        ...prev.cliente,
        [field]: value,
      },
    }));
  };

  const actualizarItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        [field]: value,
      };
      return { ...prev, items };
    });
  };

  const agregarItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { ...EMPTY_ITEM }],
    }));
  };

  const removerItem = (index) => {
    setForm((prev) => {
      if (prev.items.length === 1) return prev;
      return {
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index),
      };
    });
  };

  const resetForm = () => {
    setForm({
      tipo_factura: esMonotributista ? 'C' : 'B',
      fecha_emision: obtenerFechaHoy(),
      cliente: {
        nombre_razon_social: '',
        cuit: '',
        condicion_iva: 'CF',
        email: '',
        domicilio: '',
      },
      items: [{ ...EMPTY_ITEM }],
      condiciones_pago: 'Efectivo',
      observaciones: '',
    });
    setCondPagoCustom(false);
  };

  const validarFormulario = () => {
    if (!form.cliente.nombre_razon_social.trim()) {
      return 'Ingresá nombre o razón social del cliente.';
    }

    const itemInvalido = form.items.some(
      (item) => !item.descripcion.trim() || Number(item.cantidad) <= 0 || Number(item.precio_unitario) <= 0
    );

    if (itemInvalido) {
      return 'Revisá los ítems: descripción, cantidad e importe deben ser válidos.';
    }

    if (form.tipo_factura === 'A' && !form.cliente.cuit.trim()) {
      return 'La Factura A requiere CUIT del cliente.';
    }

    return null;
  };

  const emitirFactura = async (event) => {
    event.preventDefault();

    if (isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('emitir y autorizar facturas oficiales en AFIP');
      return;
    }

    const errorValidacion = validarFormulario();

    if (errorValidacion) {
      Swal.fire({ icon: 'warning', title: 'Datos incompletos', text: errorValidacion });
      return;
    }

    // Generar HTML con preview de la factura
    const itemsHTML = form.items.map((item, idx) => `
      <tr key=${idx} style="border-bottom: 1px solid #e5e7eb;">
        <td style="padding: 8px; text-align: left;">${item.descripcion}</td>
        <td style="padding: 8px; text-align: center;">${item.cantidad}</td>
        <td style="padding: 8px; text-align: right;">$${parseFloat(item.precio_unitario).toLocaleString('es-AR')}</td>
        <td style="padding: 8px; text-align: right;">${item.alicuota_iva}%</td>
        <td style="padding: 8px; text-align: right; font-weight: 500;">$${(Number(item.cantidad) * Number(item.precio_unitario)).toLocaleString('es-AR')}</td>
      </tr>
    `).join('');

    const previewHTML = `
      <div style="text-align: left; max-height: 400px; overflow-y: auto;">
        <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #3b82f6;">
          <p style="margin: 0; font-weight: 600; color: #1f2937;">Cliente</p>
          <p style="margin: 4px 0 0 0; color: #374151; font-size: 14px;">${form.cliente.nombre_razon_social}</p>
          ${form.cliente.cuit ? `<p style="margin: 2px 0 0 0; color: #374151; font-size: 14px;">CUIT: ${form.cliente.cuit}</p>` : ''}
        </div>

        <div style="margin-bottom: 16px;">
          <p style="margin: 0; font-weight: 600; color: #1f2937; margin-bottom: 8px;">Ítems</p>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead style="background-color: #f3f4f6;">
              <tr>
                <th style="padding: 8px; text-align: left; font-weight: 600;">Descripción</th>
                <th style="padding: 8px; text-align: center; font-weight: 600;">Cant.</th>
                <th style="padding: 8px; text-align: right; font-weight: 600;">P. Unit.</th>
                <th style="padding: 8px; text-align: right; font-weight: 600;">IVA</th>
                <th style="padding: 8px; text-align: right; font-weight: 600;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHTML}
            </tbody>
          </table>
        </div>

        <div style="background-color: #f9fafb; padding: 12px; border-radius: 6px; margin-bottom: 16px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span style="color: #374151;">Subtotal:</span>
            <span style="font-weight: 500; color: #1f2937;">$${totalPreview.subtotal.toLocaleString('es-AR', { maximumFractionDigits: 2 })}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
            <span style="color: #374151;">IVA:</span>
            <span style="font-weight: 500; color: #1f2937;">$${totalPreview.iva.toLocaleString('es-AR', { maximumFractionDigits: 2 })}</span>
          </div>
          <div style="display: flex; justify-content: space-between; border-top: 2px solid #d1d5db; padding-top: 8px;">
            <span style="font-weight: 600; color: #1f2937;">Total:</span>
            <span style="font-weight: 700; color: #1f2937; font-size: 16px;">$${totalPreview.total.toLocaleString('es-AR', { maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div style="font-size: 13px; color: #374151;">
          <p style="margin: 0; font-weight: 600;">Condiciones de pago:</p>
          <p style="margin: 4px 0 0 0;">${form.condiciones_pago}</p>
        </div>
      </div>
    `;

    // Mostrar Swal de confirmación
    const result = await Swal.fire({
      title: `Confirmar Factura ${form.tipo_factura}`,
      html: previewHTML,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Emitir y autorizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#ef4444',
      width: '600px',
    });

    if (!result.isConfirmed) return;

    try {
      setSubmitting(true);

      // Crear factura - convertir fecha_emision a datetime ISO completo
      const fechaEmisionISO = form.fecha_emision ? `${form.fecha_emision}T00:00:00` : undefined;
      const facturaData = await crearFactura({
        ...form,
        cliente_id: form.cliente_id || undefined,
        anteojo_id: form.anteojo_id || undefined,
        fecha_emision: fechaEmisionISO,
        items: form.items.map((item) => ({
          descripcion: item.descripcion,
          cantidad: Number(item.cantidad),
          precio_unitario: Number(item.precio_unitario),
          alicuota_iva: Number(item.alicuota_iva),
        })),
      });

      // Autorizar con AFIP automáticamente
      await autorizarFacturaAFIP(facturaData.id);

      await Swal.fire({
        icon: 'success',
        title: 'Factura emitida y autorizada',
        text: 'La factura se creó y fue autorizada en AFIP correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });

      resetForm();
      await cargarDatos();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error en el proceso',
        text: error.message || 'Falló la creación o autorización de la factura',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const aplicarFiltros = async () => {
    await cargarDatos(filtros);
  };

  const limpiarFiltros = async () => {
    const base = { tipo_factura: '', estado: '', page: 1, page_size: 15 };
    setFiltros(base);
    await cargarDatos(base);
  };

  const autorizarFactura = async (facturaId) => {
    if (isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('autorizar comprobantes en AFIP/ARCA');
      return;
    }
    try {
      await autorizarFacturaAFIP(facturaId);
      await Swal.fire({
        icon: 'success',
        title: 'Factura autorizada',
        text: 'La factura fue autorizada en AFIP.',
      });
      await cargarDatos();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo autorizar',
        text: error.message,
      });
    }
  };

  const handleAbrirConfiguracionAfip = () => {
    if (isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('conectar tus certificados de AFIP/ARCA y configurar puntos de venta para tu óptica');
      return;
    }
    setModalAsistenteAbierto(true);
  };

  const abrirModalNC = (factura) => {
    if (isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('emitir y autorizar notas de crédito en AFIP/ARCA');
      return;
    }
    setModalNC({
      abierto: true,
      factura,
      motivo: 'Devolución de mercadería o anulación de operación',
      subiendo: false,
    });
  };

  const cerrarModalNC = () => {
    if (modalNC.subiendo) return;
    setModalNC({
      abierto: false,
      factura: null,
      motivo: '',
      subiendo: false,
    });
  };

  const handleEmitirNotaCredito = async (e) => {
    if (e) e.preventDefault();
    if (!modalNC.factura) return;
    if (!modalNC.motivo.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Motivo requerido',
        text: 'Debés ingresar el motivo del ajuste o anulación requerido por ARCA.',
      });
      return;
    }

    const tipoNC = modalNC.factura.tipo_factura === 'A' ? 'Nota de Crédito A' : (modalNC.factura.tipo_factura === 'C' ? 'Nota de Crédito C' : 'Nota de Crédito B');
    const confirmacion = await Swal.fire({
      title: `¿Emitir ${tipoNC}?`,
      text: `Se autorizará en ARCA la Nota de Crédito por ${formatearMoneda(modalNC.factura.total)} asociada al comprobante ${modalNC.factura.punto_venta}-${modalNC.factura.numero_factura}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, emitir en ARCA',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
    });

    if (!confirmacion.isConfirmed) return;

    try {
      setModalNC((prev) => ({ ...prev, subiendo: true }));
      const res = await emitirNotaCredito({
        factura_origen_id: modalNC.factura.id,
        motivo: modalNC.motivo.trim(),
        autorizar_inmediatamente: true,
      });

      await Swal.fire({
        icon: 'success',
        title: 'Nota de Crédito emitida',
        text: `Comprobante ${res.tipo_factura} N° ${res.punto_venta}-${res.numero_factura} autorizado con CAE ${res.cae || ''}.`,
      });

      cerrarModalNC();
      await cargarDatos();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al emitir Nota de Crédito',
        text: err.message || 'No se pudo emitir la Nota de Crédito en ARCA',
      });
      setModalNC((prev) => ({ ...prev, subiendo: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" style={{ paddingTop: '20px' }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-gray-900">Facturación AFIP</h1>
          <p className="text-sm text-gray-500">Emití Factura A/B, administrá comprobantes y gestioná tus certificados desde la misma plataforma.</p>
          {afipConfig?.configurado && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-3 py-1">
                <span className="font-semibold">Punto de venta:</span> {afipConfig.punto_venta}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 rounded-full px-3 py-1">
                <span className="font-semibold">CUIT:</span> {afipConfig.cuit}
              </span>
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 border ${
                afipConfig.ambiente === 'PRODUCCIÓN'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {afipConfig.ambiente}
              </span>
              {afipConfig.razon_social && (
                <span className="text-xs text-slate-500 font-medium">
                  • {afipConfig.razon_social}
                </span>
              )}
            </div>
          )}
          {afipConfig && !afipConfig.configurado && (
            <div className="pt-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-3 py-1">
                <ExclamationTriangleIcon className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <span>AFIP no configurado — sin firma digital activa</span>
              </span>
            </div>
          )}
        </div>

        <div className="shrink-0">
          {afipConfig?.configurado ? (
            <button
              type="button"
              onClick={handleAbrirConfiguracionAfip}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl shadow-sm hover:shadow transition-all cursor-pointer"
            >
              <KeyIcon className="w-4 h-4 text-slate-500" />
              <span>Configuración y Certificados</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAbrirConfiguracionAfip}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-600/25 transition-all cursor-pointer"
            >
              <ShieldCheckIcon className="w-4 h-4 text-white" />
              <span>Configurar Facturación AFIP</span>
            </button>
          )}
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Total facturas</p>
          <p className="text-2xl font-bold">{stats?.total_facturas ?? 0}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Total facturado</p>
          <p className="text-2xl font-bold">{formatearMoneda(stats?.total_monto ?? 0)}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Pendientes</p>
          <p className="text-2xl font-bold text-amber-600">{stats?.pendientes_autorizacion ?? 0}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Rechazadas</p>
          <p className="text-2xl font-bold text-red-600">{stats?.rechazadas ?? 0}</p>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <form onSubmit={emitirFactura} className="xl:col-span-2 rounded-2xl border bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Nueva Factura</h2>
            <span className="text-xs bg-slate-100 px-2 py-1 rounded-md">Total: {formatearMoneda(totalPreview.total)}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-600">Tipo</label>
                {esMonotributista && (
                  <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                    Monotributo
                  </span>
                )}
              </div>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.tipo_factura}
                onChange={(e) => cambiarTipoFactura(e.target.value)}
                disabled={submitting || esMonotributista}
              >
                {esMonotributista ? (
                  <option value="C">Factura C (AFIP Tipo 11)</option>
                ) : (
                  <>
                    <option value="B">Factura B (Consumidor Final / Otros)</option>
                    <option value="A">Factura A (Responsable Inscripto con CUIT)</option>
                    <option value="C">Factura C</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Condición IVA</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.cliente.condicion_iva}
                onChange={(e) => actualizarCliente('condicion_iva', e.target.value)}
                disabled={submitting || esFacturaA}
              >
                <option value="CF">Consumidor Final</option>
                <option value="RI">Responsable Inscripto</option>
                <option value="MT">Monotributista</option>
                <option value="EX">Exento</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Fecha de emisión</label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2"
                value={form.fecha_emision}
                min={obtenerFechaMinima()}
                max={obtenerFechaHoy()}
                onChange={(e) => setForm((prev) => ({ ...prev, fecha_emision: e.target.value }))}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-600">Cliente / Razón Social</label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Ej: Juan Perez"
                value={form.cliente.nombre_razon_social}
                onChange={(e) => actualizarCliente('nombre_razon_social', e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">{documentoLabel}</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={documentoPlaceholder}
                  value={form.cliente.cuit}
                  onChange={(e) => actualizarCliente('cuit', e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Email</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="cliente@email.com"
                  value={form.cliente.email}
                  onChange={(e) => actualizarCliente('email', e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Items</h3>
              <button
                type="button"
                onClick={agregarItem}
                className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700"
                disabled={submitting}
              >
                + Agregar
              </button>
            </div>

            {form.items.map((item, index) => (
              <div key={`item-${index}`} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                <input
                  className="w-full border rounded-md px-2 py-1"
                  placeholder="Descripción"
                  value={item.descripcion}
                  onChange={(e) => actualizarItem(index, 'descripcion', e.target.value)}
                  disabled={submitting}
                />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-600">Cantidad</label>
                    <input
                      type="number"
                      min="1"
                      className="w-full border rounded-md px-2 py-1"
                      placeholder="Cant."
                      value={item.cantidad}
                      onChange={(e) => actualizarItem(index, 'cantidad', e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Precio</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full border rounded-md px-2 py-1"
                    placeholder={esFacturaA ? 'Precio neto' : 'Precio final'}
                    value={item.precio_unitario}
                    onChange={(e) => actualizarItem(index, 'precio_unitario', e.target.value)}
                    disabled={submitting}
                  />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-600">IVA %</label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-full border rounded-md px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder={form.tipo_factura === 'C' ? '0% (No aplica)' : (esFacturaA ? 'IVA %' : 'IVA % (incl.)')}
                        value={form.tipo_factura === 'C' ? 0 : item.alicuota_iva}
                        onChange={(e) => actualizarItem(index, 'alicuota_iva', e.target.value)}
                        disabled={submitting || form.tipo_factura === 'C'}
                        title={form.tipo_factura === 'C' ? 'En Factura C no se discrimina IVA' : 'Alícuota IVA'}
                      />
                    </div>
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 text-lg px-2 py-1"
                      onClick={() => removerItem(index)}
                      disabled={submitting || form.items.length === 1}
                      title="Remover item"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Condición de pago</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={condPagoCustom ? '__custom__' : (OPCIONES_CONDICION_PAGO.includes(form.condiciones_pago) ? form.condiciones_pago : '__custom__')}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setCondPagoCustom(true);
                    setForm((prev) => ({ ...prev, condiciones_pago: '' }));
                  } else {
                    setCondPagoCustom(false);
                    setForm((prev) => ({ ...prev, condiciones_pago: e.target.value }));
                  }
                }}
                disabled={submitting}
              >
                {OPCIONES_CONDICION_PAGO.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
                <option value="__custom__">Otro...</option>
              </select>
              {condPagoCustom && (
                <input
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  placeholder="Escribi la condición de pago"
                  value={form.condiciones_pago}
                  onChange={(e) => setForm((prev) => ({ ...prev, condiciones_pago: e.target.value }))}
                  disabled={submitting}
                  autoFocus
                />
              )}
            </div>
            <div>
              <label className="text-xs text-gray-600">Observaciones</label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.observaciones}
                onChange={(e) => setForm((prev) => ({ ...prev, observaciones: e.target.value }))}
                disabled={submitting}
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-slate-900 text-white rounded-lg py-2 font-semibold disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? 'Emitiendo...' : 'Emitir Factura'}
          </button>
        </form>

        <div className="xl:col-span-3 rounded-2xl border bg-white p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-600">Tipo</label>
              <select
                className="border rounded-lg px-3 py-2"
                value={filtros.tipo_factura}
                onChange={(e) => setFiltros((prev) => ({ ...prev, tipo_factura: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="A">Factura A</option>
                <option value="B">Factura B</option>
                <option value="C">Factura C</option>
                <option value="NCA">Nota de Crédito A</option>
                <option value="NCB">Nota de Crédito B</option>
                <option value="NCC">Nota de Crédito C</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-600">Estado</label>
              <select
                className="border rounded-lg px-3 py-2"
                value={filtros.estado}
                onChange={(e) => setFiltros((prev) => ({ ...prev, estado: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="autorizada">Autorizada</option>
                <option value="rechazada">Rechazada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>

            <button className="px-3 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700" onClick={aplicarFiltros}>
              Aplicar
            </button>
            <button className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700" onClick={limpiarFiltros}>
              Limpiar
            </button>
          </div>

          <div className="overflow-auto border rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Comprobante</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Cliente</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Fecha</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Estado</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-500" colSpan={6}>Cargando comprobantes...</td>
                  </tr>
                ) : facturas.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-500" colSpan={6}>No hay comprobantes para mostrar.</td>
                  </tr>
                ) : (
                  facturas.map((factura) => {
                    const esNC = String(factura.tipo_factura).startsWith('NC');
                    const estadoLower = String(factura.estado).toLowerCase();
                    return (
                      <tr key={factura.id} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold border ${
                              esNC
                                ? 'bg-amber-100 text-amber-800 border-amber-300'
                                : factura.tipo_factura === 'A'
                                ? 'bg-blue-100 text-blue-800 border-blue-200'
                                : factura.tipo_factura === 'C'
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                : 'bg-slate-100 text-slate-800 border-slate-200'
                            }`}>
                              {factura.tipo_factura}
                            </span>
                            <span className="font-mono text-gray-900 font-medium">
                              {`${String(factura.punto_venta).padStart(4, '0')}-${String(factura.numero_factura).padStart(8, '0')}`}
                            </span>
                          </div>
                          {factura.factura_origen_id && (
                            <span className="text-[11px] text-amber-700 block font-medium mt-0.5">
                              Ajusta Fac #{factura.factura_origen_id}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-gray-800">{factura.cliente_nombre}</td>
                        <td className="px-3 py-2.5 text-gray-600">{formatearFechaArgentina(factura.fecha_emision)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                            estadoLower === 'autorizada'
                              ? 'bg-emerald-100 text-emerald-800'
                              : estadoLower === 'pendiente'
                              ? 'bg-amber-100 text-amber-800'
                              : estadoLower === 'anulada'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {factura.estado}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{formatearMoneda(factura.total)}</td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            {estadoLower === 'pendiente' && (
                              <button
                                className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-medium flex items-center gap-1 shadow-sm transition-all"
                                onClick={() => autorizarFactura(factura.id)}
                                title="Emitir y autorizar comprobante en ARCA"
                              >
                                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                                Emitir
                              </button>
                            )}
                            {estadoLower === 'autorizada' && !esNC && (
                              <button
                                className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 text-xs font-medium flex items-center gap-1 shadow-sm transition-all"
                                onClick={() => abrirModalNC(factura)}
                                title="Emitir Nota de Crédito en ARCA para anular o ajustar esta factura"
                              >
                                <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                                Nota Crédito
                              </button>
                            )}
                            <button
                              className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium flex items-center gap-1 border border-slate-200 shadow-sm transition-all"
                              onClick={() => descargarFacturaPDF(factura.id, `${factura.punto_venta}-${factura.numero_factura}`, factura.tipo_factura)}
                              title="Descargar comprobante en PDF"
                            >
                              <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                              PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Modal para emitir Nota de Crédito */}
      {modalNC.abierto && modalNC.factura && (
        <div
          className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain animate-in fade-in duration-150"
          onClick={(e) => {
            if (e.target === e.currentTarget && !procesandoNC) {
              cerrarModalNC();
            }
          }}
        >
          <div
            className="max-w-lg w-full bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-auto max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50/50">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
                  <ArrowUturnLeftIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Emitir {modalNC.factura?.tipo_factura === 'A' ? 'Nota de Crédito A' : (modalNC.factura?.tipo_factura === 'C' ? 'Nota de Crédito C' : 'Nota de Crédito B')}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Comprobante oficial ARCA ({modalNC.factura?.tipo_factura === 'A' ? 'Cód. 03' : (modalNC.factura?.tipo_factura === 'C' ? 'Cód. 13' : 'Cód. 08')})
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={cerrarModalNC}
                disabled={modalNC.subiendo}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEmitirNotaCredito} className="p-6 space-y-4">
              {/* Resumen comprobante origen */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-2">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Factura a ajustar:</span>
                  <span className="font-semibold text-gray-900">
                    Factura {modalNC.factura.tipo_factura} N° {String(modalNC.factura.punto_venta).padStart(4, '0')}-{String(modalNC.factura.numero_factura).padStart(8, '0')}
                  </span>
                </div>
                <div className="flex justify-between items-center text-gray-600">
                  <span>Cliente:</span>
                  <span className="font-semibold text-gray-900">{modalNC.factura.cliente_nombre}</span>
                </div>
                {modalNC.factura.cae && (
                  <div className="flex justify-between items-center text-gray-600">
                    <span>CAE original:</span>
                    <span className="font-mono text-gray-700">{modalNC.factura.cae}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-slate-200 text-sm">
                  <span className="font-semibold text-gray-800">Monto total a acreditar:</span>
                  <span className="font-bold text-amber-700 text-base">{formatearMoneda(modalNC.factura.total)}</span>
                </div>
              </div>

              {/* Motivo */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700">
                  Motivo del ajuste / anulación <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 border-gray-300"
                  placeholder="Detallá el motivo (ej. Devolución de producto, error de tipeo, etc.)"
                  value={modalNC.motivo}
                  onChange={(e) => setModalNC((prev) => ({ ...prev, motivo: e.target.value }))}
                  disabled={modalNC.subiendo}
                  required
                />
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[
                    'Devolución de mercadería',
                    'Error de facturación',
                    'Descuento comercial posterior',
                    'Anulación de operación',
                  ].map((motivoSugerido) => (
                    <button
                      key={motivoSugerido}
                      type="button"
                      className="text-[11px] px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                      onClick={() => setModalNC((prev) => ({ ...prev, motivo: motivoSugerido }))}
                    >
                      {motivoSugerido}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nota fiscal */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed">
                <p className="font-semibold mb-0.5">Normativa RG 4540 (ARCA):</p>
                Al confirmar, se enviará la solicitud al Web Service de ARCA informando el comprobante asociado. La factura original pasará a estado <strong>Anulada</strong> y la Nota de Crédito quedará autorizada con su respectivo CAE y QR.
              </div>

              {/* Botones de acción */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={cerrarModalNC}
                  disabled={modalNC.subiendo}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-sm flex items-center gap-2 disabled:opacity-50 transition-all"
                  disabled={modalNC.subiendo || !modalNC.motivo.trim()}
                >
                  {modalNC.subiendo ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Autorizando en ARCA...
                    </>
                  ) : (
                    <>
                      <ArrowUturnLeftIcon className="w-4 h-4" />
                      Emitir y Autorizar en ARCA
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Asistente de Certificados y Configuración AFIP (Multi-Tenant) */}
      <ModalAsistenteAfip
        isOpen={modalAsistenteAbierto}
        onClose={() => setModalAsistenteAbierto(false)}
        onConfigGuardada={cargarDatos}
      />
    </div>
  );
};

export default Facturacion;
